/* Copyright (c) 2017 Backpack Health LLC */
/* global window */
import React from 'react';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import { SecondaryButton, CloseButton, Button, DestructiveButton } from '../buttons/Buttons';
import FlexRow from '../FlexRow';

import Spacer from '../Spacer';
import IconWarningIcons from '../icons/IconWarning';

import { actions as systemModalActions } from '../../redux/ducks/systemModal';
import { actions as consentActions } from '../../redux/ducks/consent';
import { getLoggedInUser, getProfile, getActiveProfileId, getMainProfileId, getActiveProfile } from '../../appAuthorized/redux/reduxSelectors';
import NavigationActions from '../../appAuthorized/redux/NavigationActions';
import RouteNames from '../../appAuthorized/RouteNames';
import UserService from '../../../apiServices/UserService';
import AppUtils from '../../../utilities/AppUtils';
import { actions as notificationActions } from '../../redux/ducks/systemNotification';
import { selectors as policySelectors } from '../../redux/ducks/policy';
import { selectors as groupSelectors } from '../../redux/ducks/group';
import { actions as actionItemActions } from '../../redux/ducks/actionItems';
import { withIntl } from '../../../utilities/localizedUtil';
import { removeSignupPresented } from '../../../utilities/MobileUtil';
import { actions as ownerActions } from '../../redux/ducks/owners';
import ProfileActions from '../../appAuthorized/redux/ProfileActions';
import ProfileModel, { ProfileData } from '../../../models/ProfileModel';
import { PolicyName, PolicyInterface } from '../../../models/PolicyModel';
import { Buttons } from '../../../bphComponents/bundle';

function mapStateToProps(state, routerProps) {
  const userId = getLoggedInUser(state).data.id;
  const mainProfileId = getMainProfileId(state);
  const mainProfile = getProfile(mainProfileId, state).data;
  const dependentProfileId = getActiveProfileId(state);
  const activeProfile = getActiveProfile(state).data;
  const isMainProfile = dependentProfileId === mainProfileId;
  const policy = policySelectors.getPolicyByName(state, routerProps.policyName);

  return {
    userId,
    dependentProfileId,
    activeProfile,
    mainProfile,
    isMainProfile,
    groupIdsForActiveProfile: groupSelectors.getActiveGroupIdsForProfile(state, activeProfile.id),
    policy,
    goToAccountSettings: () => {
      NavigationActions.goTo(routerProps.history, RouteNames.accountSettings());
    },
    goToSignupIntro: () => {
      removeSignupPresented(AppUtils.reloadApp);
    },
    goBack: () => {
      NavigationActions.goBack(routerProps.history);
    },
    goToProfile: () => {
      NavigationActions.goBackTo(routerProps.history, RouteNames.profile());
    },
    goBackToBackpack: () => {
      NavigationActions.replace(routerProps.history, RouteNames.myBackpack());
    },
    getPolicy: (pName) => {
      return policySelectors.getPolicyByName(state, pName);
    },
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dismiss: () => {
      dispatch(systemModalActions.dismiss());
    },
    showNotification: (messageId) => {
      dispatch(notificationActions.showNotification(messageId));
    },
    removeOwnership: (profileId, mainProfile, callback) => {
      dispatch(ownerActions.removeOwner(profileId, mainProfile.id, () => {
        ProfileActions.requestAllProfiles();
        ProfileActions.dispatch.setActiveProfile(mainProfile);

        callback();
      }));
    },
    deleteDependentProfile: (profileId, mainProfile, callback) => {
      ProfileActions.api.deleteDependentProfile(profileId, () => {
        ProfileActions.requestAllProfiles();
        ProfileActions.dispatch.setActiveProfile(mainProfile);
        callback();
      });
    },
    withdrawConsent: (profileId, policyIdList) => {
      return ProfileActions.api.withdrawConsent(profileId, policyIdList)
      .then(() => {
        dispatch(consentActions.requestAllConsents(profileId));
        return true;
      });
    },
    withdrawGroupConsent: (profileId, groupIds, policyIdList) => {
      return ProfileActions.api.withdrawGroupConsentAndLeave(profileId, groupIds, policyIdList)
      .then(() => {
        dispatch(consentActions.requestAllConsents(profileId));
        return true;
      });
    },
    switchToProfile: (profileData) => {
      return dispatch(ProfileActions.switchToProfile(profileData));
    },
    refreshActionItems: (profileId) => {
      return dispatch(actionItemActions.requestActionItems(null, profileId));
    },
  };
}

interface State {
  accountWasDeleted: boolean;
  isSubmitting: boolean;
  confirmAccountDelete: boolean;
}

export enum WithdrawName {
  tooYoungDuringSetup = 'tooYoungDuringSetup',
  tooYoungAfterSetup = 'tooYoungAfterSetup',
  duringSetup = 'duringSetup',
  afterSetup = 'afterSetup',
  duringDependentSetup = 'duringDependentSetup',
  afterDependentSetupSoleAccess = 'afterDependentSetupSoleAccess',
  afterDependentSetupSharedAccess = 'afterDependentSetupSharedAccess',
  dependentAuthorizationSoleAccess = 'dependentAuthorizationSoleAccess',
  dependentAuthorizationSharedAccess = 'dependentAuthorizationSharedAccess',
  afterSetupGroupInfoConsent = 'afterSetupGroupInfoConsent',
  afterDependentSetupGroupInfoConsent= 'afterDependentSetupGroupInfoConsent',
  afterJoinGroup = 'afterJoinGroup',
  afterDependentJoinGroup = 'afterDependentJoinGroup',
}

interface Props {
  intl: any;
  dismiss(): void;
  switchToProfile(profileData):  Promise<any>;
  showNotification(value: string): void;
  onDismiss(): void;
  userId: number;
  withdrawName: WithdrawName;
  goToAccountSettings(): void;
  goToSignupIntro(): void;
  goBack(): void;
  goToProfile(): void;
  goBackToBackpack(): void;
  removeOwnership(id, profile, callback): void;
  deleteDependentProfile(id, profile, callback): void;
  dependentProfileId: number;
  mainProfile: ProfileData;
  activeProfile: ProfileData;
  isMainProfile: boolean;
  withdrawConsent(profileId, policyIdList): Promise<any>;
  withdrawGroupConsent(profileId, groupIds, policyIdList): Promise<any>;
  policyName: PolicyName;
  policy: PolicyInterface;
  isShared: boolean;
  groupIdsForActiveProfile: number[];
  refreshActionItems(id): Promise<any>;
  hasCancelButton: boolean;
}

const phraseMessages = {
  tooYoungDuringSetup: () => {
    return {
      heading: { id: 'withdrawConsent.tooYoung.heading' },
      subHeading: { id: 'withdrawConsent.tooYoung.subHeading' },
      message: { id: 'withdrawConsent.tooYoung.message.text' },
      button: { id: 'withdrawConsent.tooYoung.confirm.button.label' },
      confirmButton: null,
    };
  },
  tooYoungAfterSetup: () => {
    return {
      heading: { id: 'withdrawConsent.forUserComplete.tooYoung.heading' },
      subHeading: { id: 'withdrawConsent.forUserComplete.tooYoung.subHeading' },
      message: { id: 'withdrawConsent.forUserComplete.tooYoung.message.text' },
      button: { id: 'withdrawConsent.forUserComplete.tooYoung.confirm.button.label' },
      confirmButton: null,
    };
  },
  duringSetup: () => {
    return {
      heading: { id: 'withdrawConsent.forUserIncomplete.heading' },
      subHeading: { id: 'withdrawConsent.forUserIncomplete.subHeading' },
      message: { id: 'withdrawConsent.forUserIncomplete.message.text' },
      button: { id: 'withdrawConsent.forUserIncomplete.confirm.button.label' },
      confirmButton: null,
    };
  },
  afterSetup: () => {
    return {
      heading: { id: 'withdrawConsent.forUserComplete.heading' },
      subHeading: { id: 'withdrawConsent.forUserComplete.subHeading' },
      message: { id: 'withdrawConsent.forUserComplete.message.text' },
      button: { id: 'withdrawConsent.forUserComplete.confirm.button.label' },
      confirmButton: { id: 'withdrawConsent.forUserComplete.delete.button.label' },
    };
  },
  duringDependentSetup: () => {
    return {
      heading: { id: 'withdrawConsent.forProfile.heading' },
      subHeading: { id: 'withdrawConsent.forProfile.subHeading' },
      message: { id: 'withdrawConsent.forProfile.soleAccess.message.text' },
      button: { id: 'withdrawConsent.forProfile.soleAccess.confirm.button.label' },
      confirmButton: null,
    };
  },
  afterDependentSetupSoleAccess: () => {
    return {
      heading: { id: 'withdrawConsent.forProfile.heading' },
      subHeading: { id: 'withdrawConsent.forProfile.subHeading' },
      message: { id: 'withdrawConsent.forProfile.soleAccess.message.text' },
      button: { id: 'withdrawConsent.forProfile.soleAccess.confirm.button.label' },
      confirmButton: { id: 'withdrawConsent.forProfile.delete.button.label' },
    };
  },
  afterDependentSetupSharedAccess: () => {
    return {
      heading: { id: 'withdrawConsent.forProfile.heading' },
      subHeading: { id: 'withdrawConsent.forProfile.subHeading' },
      message: { id: 'withdrawConsent.forProfile.sharedAccess.message.text' },
      button: { id: 'withdrawConsent.forProfile.sharedAccess.confirm.button.label' },
      confirmButton: null,
    };
  },
  dependentAuthorizationSoleAccess: () => {
    return {
      heading: { id: 'dependentAuth.withdraw.heading' },
      subHeading: { id: 'dependentAuth.withdraw.subheading' },
      message: { id: 'dependentAuth.withdraw.NotShared.message' },
      button: { id: 'dependentAuth.withdraw.NotShared.confirm.button.label' },
      confirmButton: { id: 'withdrawConsent.forProfile.delete.button.label' },
    };
  },
  dependentAuthorizationSharedAccess: () => {
    return {
      heading: { id: 'dependentAuth.withdraw.heading' },
      subHeading: { id: 'dependentAuth.withdraw.subheading' },
      message: { id: 'dependentAuth.withdraw.Shared.message' },
      button: { id: 'dependentAuth.withdraw.Shared.confirm.button.label' },
      confirmButton: null,
    };
  },
  afterSetupGroupInfoConsent: () => {
    return {
      heading: { id: 'profileGroupConsent.withdraw.user.heading' },
      subHeading: null,
      message: { id: 'profileGroupConsent.withdraw.user.message' },
      button: { id: 'profileGroupConsent.withdraw.confirm.button.label' },
      confirmButton: { id: 'withdrawConsent.forProfile.delete.button.label' },
    };
  },
  afterDependentSetupGroupInfoConsent: (dependentProfile?) => {
    const dependentName = ProfileModel.getDisplayName(dependentProfile);

    return {
      heading: { id: 'profileGroupConsent.withdraw.notUser.heading', values: { string1: dependentName } },
      subHeading: null,
      message: { id: 'profileGroupConsent.withdraw.notUser.message', values: { string1: dependentName, string2: dependentName, string3: dependentName } },
      button: { id: 'profileGroupConsent.withdraw.confirm.button.label' },
      confirmButton: { id: 'withdrawConsent.forProfile.delete.button.label' },
    };
  },
};

@withIntl
class DoNotConsentModal extends React.Component<Props, State> {

  state: State = {
    accountWasDeleted: false,
    confirmAccountDelete: false,
    isSubmitting: false,
  };

  onDismiss = () => {
    this.props.dismiss();

    if (this.props.onDismiss) { this.props.onDismiss(); }
  }

  deleteAccount = () => {
    this.setState({ isSubmitting: true }, () => {
      UserService.del(this.props.userId, {}, (apiError) => {
        this.props.dismiss();

        if (apiError) {
          this.setState({ isSubmitting: false });
          this.props.showNotification(apiError.messageKey());
          return;
        }

        const backpackHomeUrl = this.props.intl.formatMessage({ id: 'backpackHealth.url.url' });

        AppUtils.logout({}, () => {
          if (IS_ANDROID_BUILD) {
            this.props.goToSignupIntro();
          } else {
            window.location.replace(backpackHomeUrl);
          }
        });
      });
    });
  }

  withdrawConsent = () => {
    const profileId = this.props.isMainProfile ? this.props.mainProfile.id : this.props.dependentProfileId;
    return this.props.withdrawConsent(profileId, [this.props.policy.id]);
  }

  withdrawGroupConsent = () => {
    const profileId = this.props.isMainProfile ? this.props.mainProfile.id : this.props.dependentProfileId;
    return this.props.withdrawGroupConsent(profileId, this.props.groupIdsForActiveProfile, [this.props.policy.id]);
  }

  deleteDependentProfile = () => {
    this.props.deleteDependentProfile(this.props.dependentProfileId, this.props.mainProfile, this.goToMainProfile);
  }

  removeOwnership = () => {
    this.props.removeOwnership(this.props.dependentProfileId, this.props.mainProfile, this.goToMainProfile);
  }

  goToMainProfile = () => {
    this.onDismiss();
    this.props.switchToProfile(this.props.mainProfile);
    this.props.goBackToBackpack();
  }

  goToProfile = () => {
    this.onDismiss();

    this.props.switchToProfile(this.props.activeProfile)
    .then(() => {
      this.props.goBackToBackpack();
    });
  }

  firstAction = () => {
    switch (this.props.withdrawName) {
      case WithdrawName.tooYoungDuringSetup:
      case WithdrawName.duringSetup:
        this.withdrawConsent().then(this.deleteAccount);
        break;
      case WithdrawName.tooYoungAfterSetup:
      case WithdrawName.afterSetup:
      case WithdrawName.afterDependentSetupSoleAccess:
        this.setState({ confirmAccountDelete: true });
        break;
      case WithdrawName.duringDependentSetup:
        this.goToMainProfile();
        break;
      case WithdrawName.afterDependentSetupSharedAccess:
        this.withdrawConsent().then(this.removeOwnership);
        break;
      case WithdrawName.afterDependentJoinGroup:
      case WithdrawName.afterJoinGroup:
        this.withdrawGroupConsent()
          .then(() => this.props.refreshActionItems(this.props.activeProfile.id))
          .then(this.goToProfile);
        break;
      default:
        break;
    }
  }

  confirmedAction = () => {
    switch (this.props.withdrawName) {
      case WithdrawName.tooYoungAfterSetup:
      case WithdrawName.afterSetup:
        this.withdrawConsent().then(this.deleteAccount);
        break;
      case WithdrawName.afterDependentSetupSoleAccess:
        this.withdrawConsent().then(this.deleteDependentProfile);
        break;
      case WithdrawName.tooYoungDuringSetup:
      case WithdrawName.duringSetup:
      case WithdrawName.afterDependentSetupSharedAccess:
      case WithdrawName.duringDependentSetup:
      default:
        break;
    }
  }

  getPhraseMessages = () => {
    const { withdrawName, policyName, isShared, isMainProfile, activeProfile } = this.props;

    if (policyName === PolicyName.DEPENDENT_AUTHORIZATION && isShared) {
      return phraseMessages[WithdrawName.dependentAuthorizationSharedAccess]();
    } else if (policyName === PolicyName.DEPENDENT_AUTHORIZATION) {
      return phraseMessages[WithdrawName.dependentAuthorizationSoleAccess]();
    } else if (policyName === PolicyName.GROUP_CONSENT) {
      if (isMainProfile) {
        return phraseMessages[WithdrawName.afterSetupGroupInfoConsent]();
      } else {
        return phraseMessages[WithdrawName.afterDependentSetupGroupInfoConsent](activeProfile);
      }
    } else {
      return phraseMessages[withdrawName]();
    }
  }

  render() {
    if (this.state.accountWasDeleted) {
      return <Redirect to={RouteNames.deleteAccountSuccess()} />;
    }
    const { confirmAccountDelete } = this.state;
    const { hasCancelButton } = this.props;

    const messages = this.getPhraseMessages();

    return (
      <div className="cmp-warningmodal">
        <FlexRow style={{ alignItems: 'flex-start' }}>
          <div><IconWarningIcons.XLarge strokeClassName="stroke-color-profile-accent" /></div>
          <CloseButton align="right" verticalAlign="top" className="bkgd-color-gsMiddleDT" onClick={this.onDismiss} />
        </FlexRow>

        <Spacer size="12" />

        <div className="font-title-2 font-color-profile">
          <FormattedMessage {...messages.heading} />
        </div>

        {
          messages.subHeading && (
            <>
              <Spacer size="4" />
              <div className="font-headline font-color-gsDark">
                <FormattedMessage {...messages.subHeading} />
              </div>
            </>
          )
        }

        <Spacer size="12" />

        <div className="font-subhead font-color-gsDark">
          <FormattedMessage {...messages.message} />
        </div>

        <Spacer size="16" />

        {
          confirmAccountDelete ? (
            <DestructiveButton
              className="font-title-2"
              onClick={this.confirmedAction}
            >
              <FormattedMessage {...messages.confirmButton} />
            </DestructiveButton>
          ) : (
            <SecondaryButton className="font-title-2 font-color-profile-accent border-profile-accent" onClick={this.firstAction}>
              <FormattedMessage {...messages.button} />
            </SecondaryButton>
          )
        }

        {
          hasCancelButton ? (
            <Buttons.ButtonLink onClick={this.onDismiss} className="font-color-gsMedDark font-subhead" style={{ marginTop: 24, display: 'block', textDecoration: 'underline' }}>
              <FormattedMessage id="nav.cancel.button.label" />
            </Buttons.ButtonLink>
          ) : null
        }
      </div>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DoNotConsentModal));
